/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import { getDatabase, child, get, ref, set, onChildAdded, onChildChanged, onChildRemoved, push } from "firebase/database";
import { realtime } from "../../../firebaseConfig";
import CommentIcon from "@mui/icons-material/Comment";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { getRelativeTime, getDateTime } from "../../utils/time";
import { getInitialsFromName } from "../../utils/names";
import "./Comments.css"; // Assuming you have a Comments.css file

function Comments({ viewId, componentId, currentUser }) {
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [comments, setComments] = useState();
  const [updateCommentsListeners, setUpdateCommentsListeners] = useState(false);
  const [isUnread, setIsUnread] = useState(false);
  const [newComment, setNewComment] = useState("");

  // === create listeners for collaborator comments ==== \\
  useEffect(() => {
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") return;

    const commentsEntry = `collabLayer/views/${viewId}/components/${componentId}/comments`;
    const commentsRef = ref(realtime, commentsEntry);

    const initializeComments = async () => {
      // Check if comments exist
      const dbRef = ref(getDatabase());
      let commentsSnapshot = await get(child(dbRef, commentsEntry));
      if (commentsSnapshot.exists()) {
        // Comments found
        const commentsArray = [];
        commentsSnapshot.forEach((childSnapshot) => {
          commentsArray.push(childSnapshot.val());
        });
        setComments(commentsArray);
      } else {
        // No comments found
        setComments([]);
      }
    };

    const scrollToBottom = () => {
      const commentsElement = document.getElementById(`comments-${componentId}`);
      if (commentsElement) {
        setTimeout(() => {
          commentsElement.scrollTo({ top: commentsElement.scrollHeight, left: 0, behavior: "smooth" });
        }, "100");
      }
    };

    const createListenersForComments = async () => {
      // Create listener for comments onChildAdded
      onChildAdded(commentsRef, (data) => {
        const comment = data.val();
        // Add the new comment to the comments array if id doesn't exist
        const updatedComments = [...comments, comment];
        if (!isCommentOpen) setIsUnread(true);
        setComments(updatedComments);
        scrollToBottom();
      });

      // Create listener for comments onChildChanged
      onChildChanged(commentsRef, (data) => {
        const comment = data.val();
        const updatedComments = comments ?? comments.map((c) => (c.id === comment.id ? comment : c));
        setComments(updatedComments);
        scrollToBottom();
      });

      // Create listener for comments onChildRemoved
      onChildRemoved(commentsRef, (data) => {
        const comment = data.val();
        const updatedComments = comments ?? comments.filter((c) => c.id !== comment.id);
        setComments(updatedComments);
        scrollToBottom();
      });
    };

    if (!comments) {
      initializeComments();
    } else if (!updateCommentsListeners) {
      createListenersForComments();
      setUpdateCommentsListeners(true);
    }
  }, [comments, componentId, isCommentOpen, updateCommentsListeners, viewId]);

  const handleCommentButtonClick = () => {
    setIsCommentOpen(!isCommentOpen);
    if (!isCommentOpen && isUnread) setIsUnread(false);
  };

  const handleSubmit = () => {
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      console.warn("Comments disabled on localhost.");
      setNewComment("");
      return;
    }

    if (newComment.trim() !== "") {
      const newDate = new Date();
      const newCommentObj = {
        id: newDate.getTime(),
        text: newComment,
        uid: currentUser.uid,
        displayName: currentUser.displayName,
        createdAt: newDate.getTime(),
      };
      // Add the new comment to the realtime database
      const commentsEntry = `collabLayer/views/${viewId}/components/${componentId}/comments`;
      const commentsRef = ref(realtime, commentsEntry);
      const addCommentRef = push(commentsRef);
      set(addCommentRef, newCommentObj); // This will trigger the onChildAdded listener
      setNewComment("");
    }
  };

  return (
    <div className="component-comments">
      <CommentIcon className={`commentButton${isUnread ? " unread" : ""}`} onClick={handleCommentButtonClick} />
      {isCommentOpen && (
        <div
          style={{
            position: "absolute",
            top: "-10px",
            left: "40px",
            transform: "translateY(0%)",
            backgroundColor: "#d8d8d4",
            padding: "8px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            width: "325px",
            zIndex: "110",
          }}
        >
          {comments?.length > 0 ? (
            <div
              id={`comments-${componentId}`}
              className="comments-container"
              style={{ height: "300px", overflowY: "scroll", padding: "0px", overflow: "scroll", scrollBarWidth: "thin" }}
            >
              {comments?.map((comment) => (
                <div
                  key={comment.id}
                  id={`comment-${componentId}-${comment.id}`}
                  className="comment"
                  style={{ padding: "8px", color: "#333", backgroundColor: "#eee", borderRadius: "8px" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "11px", color: "#888" }}>
                    <span
                      style={{
                        fontWeight: "bold",
                        backgroundColor: "#919fa9",
                        color: "#eee",
                        width: "20px",
                        height: "18px",
                        paddingTop: "1px",
                        textAlign: "center",
                        borderRadius: "20px",
                      }}
                      title={comment.displayName}
                    >
                      {getInitialsFromName(comment.displayName)}
                    </span>
                    <span title={getDateTime(comment.createdAt)}>{getRelativeTime(comment.createdAt)}</span>
                  </div>
                  {comment.text}
                </div>
              ))}
            </div>
          ) : null}
          <div className="comments-form">
            <TextField multiline fullWidth value={newComment} onChange={(e) => setNewComment(e.target.value)} label="Add a comment" />
            <Button onClick={handleSubmit}>Submit</Button>
          </div>
          <div className="comments-caret">
            <svg viewBox="0 -256 1792 1792" id="svg3001" version="1.1" width="100%" height="100%">
              <g transform="matrix(1,0,0,-1,478.37288,1255.0508)" id="g3003">
                <path
                  d="M 640,1088 V 192 q 0,-26 -19,-45 -19,-19 -45,-19 -26,0 -45,19 L 83,595 q -19,19 -19,45 0,26 19,45 l 448,448 q 19,19 45,19 26,0 45,-19 19,-19 19,-45 z"
                  id="path3005"
                  style={{ fill: "currentColor" }}
                />
              </g>
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

export default Comments;
